// ===== 1. Imports =====
import React, { useEffect, useMemo, useState } from "react";
import { UserPlus, Trash, Clipboard, ClipboardCheck } from "lucide-react";
import Modal from "../components/Modal";
import emailjs from "emailjs-com";
import { db, auth } from "../firebaseConfig";
import {
  collection,
  doc,
  setDoc,
  deleteDoc,
  onSnapshot,
  serverTimestamp,
} from "firebase/firestore";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { EMAILJS_CONFIG } from "../emailjsConfig";

// ===== 2. Utilidades =====
const generatePassword = (length = 10) => {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%";
  return Array.from({ length }, () =>
    chars.charAt(Math.floor(Math.random() * chars.length))
  ).join("");
};

const byPropAsc = (prop) => (a, b) =>
  String(a?.[prop] || "").localeCompare(String(b?.[prop] || ""), "es", {
    sensitivity: "base",
    numeric: true,
  });

// ===== 3. Componente Principal =====
export default function UsersPage({ projectCode, userRole }) {
  const [admins, setAdmins] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [showDeptModal, setShowDeptModal] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [toast, setToast] = useState(null);
  const [sending, setSending] = useState(false);
  const [copiedEmail, setCopiedEmail] = useState(null);

  const showToast = (msg, error = false) => {
    setToast({ msg, error });
    setTimeout(() => setToast(null), 3000);
  };

  // ===== 4. Configuración EmailJS (usando config.js) =====
  const { SERVICE_ID, TEMPLATE_ID, PUBLIC_KEY } = EMAILJS_CONFIG;

  // ===== 5. Listeners Firestore =====
  useEffect(() => {
    if (!projectCode) return;
    const usersRef = collection(db, "projects", projectCode, "users");
    const unsubUsers = onSnapshot(usersRef, (snap) => {
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      list.sort(byPropAsc("name"));
      setUsuarios(list);
      setAdmins(list.filter((u) => ["PM", "Controller"].includes(u.role)));
    });
    const deptRef = collection(db, "projects", projectCode, "departamentos");
    const unsubDepts = onSnapshot(deptRef, (snap) => {
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      list.sort(byPropAsc("name"));
      setDepartamentos(list);
    });
    return () => {
      unsubUsers();
      unsubDepts();
    };
  }, [projectCode]);

  const usersByDept = useMemo(() => {
    const map = {};
    departamentos.forEach((d) => (map[d.name] = []));
    usuarios
      .filter((u) => !["PM", "Controller"].includes(u.role))
      .forEach((u) => {
        const key = u.department || "_SIN_DEPARTAMENTO_";
        if (!map[key]) map[key] = [];
        map[key].push(u);
      });
    Object.keys(map).forEach((k) => map[k].sort(byPropAsc("name")));
    return map;
  }, [usuarios, departamentos]);

  // ===== 6. Copiar contraseña =====
  const handleCopyPassword = async (user) => {
    try {
      await navigator.clipboard.writeText(user.password);
      setCopiedEmail(user.email);
      showToast(`Contraseña copiada: ${user.email}`);
      setTimeout(() => setCopiedEmail(null), 2000);
    } catch (err) {
      console.error("Error copiando contraseña:", err);
      showToast("No se pudo copiar la contraseña", true);
    }
  };

  // ===== 7. Eliminar usuario =====
  const handleDeleteUser = async (email) => {
    if (!window.confirm(`¿Seguro que quieres eliminar al usuario "${email}"?`))
      return;
    try {
      await deleteDoc(doc(db, "projects", projectCode, "users", email));
      showToast(`Usuario eliminado: ${email}`);
    } catch (err) {
      console.error("Error al eliminar usuario:", err);
      showToast("Error al eliminar usuario", true);
    }
  };

  // ===== 8. Enviar invitación EmailJS =====
  const sendInviteEmail = async ({ name, email, role, password }) => {
    if (!email) return;
    setSending(true);
    try {
      await emailjs.send(
        SERVICE_ID,
        TEMPLATE_ID,
        {
          project_name: projectCode || "Sin código",
          project_code: projectCode || "Sin código",
          user_email: email,
          temp_password: password,
          user_name: name || "",
          user_role: role || "",
        },
        PUBLIC_KEY
      );
      showToast(`Invitación enviada a ${email}`);
    } catch (err) {
      console.error("Error enviando invitación:", err);
      showToast("Error al enviar la invitación", true);
    } finally {
      setSending(false);
    }
  };

  // ===== 9. Crear Administrador =====
  const handleSaveAdmin = async (e) => {
    e.preventDefault();
    const form = new FormData(e.target);
    const name = form.get("name").trim();
    const email = form.get("email").trim();
    const role = form.get("role");
    if (!name || !email || !role) return;
    const tempPassword = generatePassword();
    try {
      await createUserWithEmailAndPassword(auth, email, tempPassword);
      await setDoc(doc(db, "projects", projectCode, "users", email), {
        name,
        email,
        role,
        password: tempPassword,
        createdAt: serverTimestamp(),
      });
      await sendInviteEmail({ name, email, role, password: tempPassword });
      showToast("Administrador creado correctamente");
      setShowAdminModal(false);
    } catch (err) {
      console.error("Error creando admin:", err);
      showToast("Error al crear administrador", true);
    }
  };

  // ===== 10. Crear Usuario =====
  const handleSaveUser = async (e) => {
    e.preventDefault();
    const form = new FormData(e.target);
    const name = form.get("name").trim();
    const email = form.get("email").trim();
    const role = form.get("role");
    const department = editTarget?.deptName;
    if (!name || !email || !role || !department) return;
    const tempPassword = generatePassword();
    try {
      await createUserWithEmailAndPassword(auth, email, tempPassword);
      await setDoc(doc(db, "projects", projectCode, "users", email), {
        name,
        email,
        role,
        department,
        password: tempPassword,
        createdAt: serverTimestamp(),
      });
      await sendInviteEmail({ name, email, role, password: tempPassword });
      showToast("Usuario creado correctamente");
      setShowUserModal(false);
      setEditTarget(null);
    } catch (err) {
      console.error("Error creando usuario:", err);
      showToast("Error al crear usuario", true);
    }
  };

  // ===== 11. Crear Departamento =====
  const handleSaveDepartment = async (e) => {
    e.preventDefault();
    const name = e.target.name.value.trim();
    if (!name) return;
    try {
      await setDoc(doc(db, "projects", projectCode, "departamentos", name), {
        name,
        createdAt: serverTimestamp(),
      });
      showToast("Departamento creado");
      setShowDeptModal(false);
    } catch (err) {
      console.error("Error creando departamento:", err);
      showToast("Error al crear el departamento", true);
    }
  };

  // ===== 12. RoleBadge =====
  const RoleBadge = ({ role }) => {
    const colors = {
      PM: "var(--accent)",
      Controller: "#475569",
      HOD: "var(--accent)",
      Coordinator: "#16a34a",
      Crew: "#6b7280",
    };
    return (
      <span
        style={{
          background: colors[role] || "#999",
          color: "#fff",
          padding: "3px 8px",
          borderRadius: 12,
          fontSize: 12,
          marginLeft: 8,
        }}
      >
        {role}
      </span>
    );
  };

  // ===== 12. Render =====
  return (
    <section
      className="container"
      style={{
        padding: "40px 0",
        maxWidth: 1600,
        display: "grid",
        gridTemplateColumns: "1fr 3fr",
        gap: "24px",
        alignItems: "start",
      }}
    >
      <div className="card" style={{ padding: 24 }}>
        <h2>Administradores</h2>
        <div style={{ textAlign: "right", margin: "10px 0" }}>
          <button
            className="button button--primary"
            onClick={() => setShowAdminModal(true)}
          >
            <UserPlus size={16} style={{ marginRight: 6 }} /> Nuevo admin
          </button>
        </div>
        {admins.length === 0 ? (
          <div className="empty">No hay administradores todavía.</div>
        ) : (
          admins.map((a) => (
            <div
              key={a.email}
              className="card"
              style={{
                padding: 16,
                marginBottom: 10,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <strong>{a.name}</strong>
                <RoleBadge role={a.role} />
                <div style={{ fontSize: 13, color: "var(--muted)" }}>
                  {a.email}
                </div>
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                <button
                  className="button button--ghost"
                  onClick={() => handleCopyPassword(a)}
                  title="Copiar contraseña"
                >
                  {copiedEmail === a.email ? (
                    <ClipboardCheck size={16} />
                  ) : (
                    <Clipboard size={16} />
                  )}
                </button>
                <button
                  className="button button--ghost"
                  onClick={() => handleDeleteUser(a.email)}
                  title="Eliminar usuario"
                >
                  <Trash size={16} />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="card" style={{ padding: 24 }}>
        <h2>Departamentos</h2>
        <div style={{ textAlign: "right", margin: "10px 0" }}>
          <button
            className="button button--primary"
            onClick={() => setShowDeptModal(true)}
          >
            Nuevo departamento
          </button>
        </div>
        {departamentos.map((dept) => {
          const deptUsers = usersByDept[dept.name] || [];
          return (
            <div
              key={dept.id}
              className="card"
              style={{ padding: 20, marginBottom: 20 }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <h3 style={{ margin: 0 }}>{dept.name}</h3>
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    className="button button--primary"
                    onClick={() => {
                      setEditTarget({ deptId: dept.id, deptName: dept.name });
                      setShowUserModal(true);
                    }}
                  >
                    <UserPlus size={16} style={{ marginRight: 6 }} /> Nuevo
                    usuario
                  </button>
                  <button
                    className="button button--ghost"
                    onClick={() =>
                      deleteDoc(
                        doc(
                          db,
                          "projects",
                          projectCode,
                          "departamentos",
                          dept.id
                        )
                      )
                    }
                    title="Eliminar departamento"
                  >
                    <Trash size={16} />
                  </button>
                </div>
              </div>
              {deptUsers.length === 0 ? (
                <div className="empty">Sin usuarios en este departamento.</div>
              ) : (
                deptUsers.map((u) => (
                  <div
                    key={u.email}
                    style={{
                      border: "1px solid var(--ring)",
                      borderRadius: 8,
                      padding: "8px 12px",
                      marginTop: 10,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div>
                      <strong>{u.name}</strong>
                      <RoleBadge role={u.role} />
                      <div style={{ fontSize: 13, color: "var(--muted)" }}>
                        {u.email}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button
                        className="button button--ghost"
                        onClick={() => handleCopyPassword(u)}
                        title="Copiar contraseña"
                      >
                        {copiedEmail === u.email ? (
                          <ClipboardCheck size={16} />
                        ) : (
                          <Clipboard size={16} />
                        )}
                      </button>
                      <button
                        className="button button--ghost"
                        onClick={() => handleDeleteUser(u.email)}
                        title="Eliminar usuario"
                      >
                        <Trash size={16} />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          );
        })}
      </div>

      {showAdminModal && (
        <Modal
          title="Nuevo administrador"
          onClose={() => setShowAdminModal(false)}
        >
          <form className="stack" onSubmit={handleSaveAdmin}>
            <div className="field">
              <label>Nombre del administrador</label>
              <input name="name" type="text" required />
            </div>
            <div className="field">
              <label>Correo electrónico</label>
              <input name="email" type="email" required />
            </div>
            <div className="field">
              <label>Rol</label>
              <select name="role" required>
                <option value="PM">PM</option>
                <option value="Controller">Controller</option>
              </select>
            </div>
            <button type="submit" className="button button--primary">
              Guardar y enviar
            </button>
          </form>
        </Modal>
      )}

      {showDeptModal && (
        <Modal
          title="Nuevo departamento"
          onClose={() => setShowDeptModal(false)}
        >
          <form className="stack" onSubmit={handleSaveDepartment}>
            <div className="field">
              <label>Nombre del departamento</label>
              <input name="name" type="text" required />
            </div>
            <button type="submit" className="button button--primary">
              Guardar
            </button>
          </form>
        </Modal>
      )}

      {showUserModal && (
        <Modal title="Nuevo usuario" onClose={() => setShowUserModal(false)}>
          <form className="stack" onSubmit={handleSaveUser}>
            <div className="field">
              <label>Nombre del usuario</label>
              <input name="name" type="text" required />
            </div>
            <div className="field">
              <label>Correo electrónico</label>
              <input name="email" type="email" required />
            </div>
            <div className="field">
              <label>Rol</label>
              <select name="role" required>
                <option value="HOD">HOD</option>
                <option value="Coordinator">Coordinator</option>
                <option value="Crew">Crew</option>
              </select>
            </div>
            <button type="submit" className="button button--primary">
              Guardar y enviar
            </button>
          </form>
        </Modal>
      )}

      {toast && (
        <div
          style={{
            position: "fixed",
            bottom: 20,
            right: 20,
            background: toast.error ? "#dc2626" : "var(--accent)",
            color: "#fff",
            padding: "12px 20px",
            borderRadius: 10,
            fontSize: 14,
            fontWeight: 500,
          }}
        >
          {toast.msg}
        </div>
      )}
    </section>
  );
}
